// video-feature-tabs.js
class VideoFeatureTabs extends HTMLElement {
  connectedCallback() {
    if (this._videoFeatureReady) return;
    this._videoFeatureReady = true;

    const items = Array.from(this.querySelectorAll('video-feature-tab')).map((tab, index) => ({
      index,
      title: tab.getAttribute('title') || '',
      video: tab.getAttribute('video') || '',
      videoMobile: tab.getAttribute('video-mobile') || '',
      poster: tab.getAttribute('poster') || '',
      posterMobile: tab.getAttribute('poster-mobile') || '',
      icon: tab.innerHTML.trim()
    }));

    if (!items.length) return;

    this.innerHTML = this.createMarkup(items);
    this.init(items);
  }

  createMarkup(items) {
    const tabsMarkup = items.map(item => `
      <button class="video-feature-tab${item.index === 0 ? ' video-feature-is-active' : ''}" type="button" data-index="${item.index}" aria-selected="${item.index === 0}">
        <span class="video-feature-tab-icon" aria-hidden="true">${item.icon}</span>
        <span class="video-feature-tab-title"></span>
      </button>
    `).join('');

    const panelsMarkup = items.map(item => `
      <div class="video-feature-panel${item.index === 0 ? ' video-feature-is-active' : ''}" data-index="${item.index}" aria-hidden="${item.index === 0 ? 'false' : 'true'}">
        <div class="video-feature-poster"></div>
        ${this.createControlsMarkup()}
      </div>
    `).join('');

    return `
      <section class="video-feature-section">
        <div class="video-feature-tabs-scroller" data-video-feature-tabs-scroller>
          <div class="video-feature-tabs" data-video-feature-tabs>
            <span class="video-feature-tab-indicator" data-video-feature-tab-indicator></span>
            ${tabsMarkup}
          </div>
        </div>
        <div class="video-feature-stage" data-video-feature-stage>
          ${panelsMarkup}
        </div>
      </section>
    `;
  }

  createControlsMarkup() {
    return `
      <button class="video-feature-click-layer" type="button" aria-label="播放或暂停视频"></button>
      <span class="video-feature-big-play-icon" aria-hidden="true"></span>
      <div class="video-feature-controls">
        <button class="video-feature-control-btn video-feature-control-play" type="button" aria-label="播放或暂停">
          <svg class="video-feature-play-icon" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" fill="currentColor" stroke="none"></path></svg>
          <svg class="video-feature-pause-icon" viewBox="0 0 24 24"><path d="M8 5v14M16 5v14"></path></svg>
        </button>
        <div class="video-feature-progress" role="slider" aria-label="视频进度" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
          <div class="video-feature-progress-track"><div class="video-feature-progress-bar"></div></div>
        </div>
        <span class="video-feature-time">0:00 / 0:00</span>
        <button class="video-feature-control-btn video-feature-control-fullscreen" type="button" aria-label="全屏">
          <svg viewBox="0 0 24 24"><path d="M8 3H3v5"></path><path d="M16 3h5v5"></path><path d="M8 21H3v-5"></path><path d="M16 21h5v-5"></path></svg>
        </button>
      </div>
    `;
  }

  init(items) {
    this.items = items;
    this.activeIndex = 0;
    this.dragState = null;
    this.blockNextClick = false;

    this.scroller = this.querySelector('[data-video-feature-tabs-scroller]');
    this.tabsWrap = this.querySelector('[data-video-feature-tabs]');
    this.indicator = this.querySelector('[data-video-feature-tab-indicator]');
    this.stage = this.querySelector('[data-video-feature-stage]');
    this.tabs = Array.from(this.querySelectorAll('.video-feature-tab'));
    this.panels = Array.from(this.querySelectorAll('.video-feature-panel'));

    this.tabsWrap.style.setProperty('--video-feature-tab-count', this.tabs.length);

    this.tabs.forEach((tab, index) => {
      tab.querySelector('.video-feature-tab-title').textContent = this.items[index].title;
      tab.addEventListener('click', event => this.onTabClick(event, index));
    });

    this.panels.forEach((panel, index) => {
      panel._videoFeatureControlsTimer = null;
      this.setPoster(panel, this.items[index]);
      this.bindPanel(panel);
      this.updateProgress(panel);
    });

    this.bindDragEvents();
    this.bindResizeEvents();

    requestAnimationFrame(() => {
      this.updateTabsAlign();
      this.updateIndicator();
      this.switchTo(this.activeIndex);
    });
  }

  byIndex(list, index) {
    return list.find(item => Number(item.dataset.index) === index);
  }

  get activeTab() {
    return this.byIndex(this.tabs, this.activeIndex);
  }

  get activePanel() {
    return this.byIndex(this.panels, this.activeIndex);
  }

  getMediaByQuery(desktopValue, mobileValue) {
    const useMobile = window.matchMedia('(max-width: 769px)').matches;
    return useMobile && mobileValue ? mobileValue : desktopValue;
  }

  getVideoSrc(item) {
    return this.getMediaByQuery(item.video, item.videoMobile);
  }

  getPosterSrc(item) {
    return this.getMediaByQuery(item.poster, item.posterMobile);
  }

  setPoster(panel, item) {
    const poster = panel.querySelector('.video-feature-poster');
    if (!poster) return;

    const src = this.getPosterSrc(item);
    poster.style.backgroundImage = src ? `url("${src}")` : '';
  }

  ensureVideo(panel, item) {
    let video = panel.querySelector('video');
    if (video) return video;

    const src = this.getVideoSrc(item);
    if (!src) return null;

    video = document.createElement('video');
    video.src = src;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('webkit-playsinline', '');

    const poster = this.getPosterSrc(item);
    if (poster) video.poster = poster;

    video.addEventListener('loadeddata', () => {
      panel.classList.add('video-feature-video-ready');
    });
    video.addEventListener('canplay', () => {
      panel.classList.add('video-feature-video-ready');
    });
    video.addEventListener('play', () => this.syncState(panel));
    video.addEventListener('pause', () => this.syncState(panel));
    video.addEventListener('ended', () => this.syncState(panel));
    video.addEventListener('loadedmetadata', () => this.updateProgress(panel));
    video.addEventListener('timeupdate', () => this.updateProgress(panel));

    const posterEl = panel.querySelector('.video-feature-poster');
    if (posterEl) {
      posterEl.insertAdjacentElement('afterend', video);
    } else {
      panel.prepend(video);
    }

    return video;
  }

  unloadVideo(panel) {
    const video = panel.querySelector('video');
    if (!video) return;

    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();

    panel.classList.remove('video-feature-video-ready');
    panel.classList.remove('video-feature-is-playing');
    panel.classList.add('video-feature-is-paused');
    this.updateProgress(panel);
  }

  enableActiveSound() {
    const video = this.activePanel?.querySelector('video');
    if (!video) return;
    video.muted = false;
    video.volume = 1;
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
    return `${minutes}:${secs}`;
  }

  waitReady(video) {
    return new Promise(resolve => {
      if (video.readyState >= 3) {
        resolve();
        return;
      }

      const done = () => {
        video.removeEventListener('canplay', done);
        video.removeEventListener('loadeddata', done);
        resolve();
      };

      video.addEventListener('canplay', done, { once: true });
      video.addEventListener('loadeddata', done, { once: true });
    });
  }

  updateTabsAlign() {
    this.tabsWrap.style.justifyContent = this.tabsWrap.scrollWidth <= this.scroller.clientWidth ? 'center' : 'flex-start';
  }

  updateIndicator() {
    const tab = this.activeTab;
    if (!tab) return;

    this.updateTabsAlign();

    const tabsRect = this.tabsWrap.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const gap = window.innerWidth <= 769 ? 18 : 34;

    this.indicator.style.width = `${Math.max(0, tabRect.width - gap * 2)}px`;
    this.indicator.style.transform = `translateX(${tabRect.left - tabsRect.left + gap}px)`;
  }

  updateProgress(panel) {
    const video = panel.querySelector('video');
    const progress = panel.querySelector('.video-feature-progress');
    const bar = panel.querySelector('.video-feature-progress-bar');
    const time = panel.querySelector('.video-feature-time');

    if (!progress || !bar) return;

    const duration = video?.duration || 0;
    const current = video?.currentTime || 0;
    const percent = duration ? Math.min(100, (current / duration) * 100) : 0;

    bar.style.width = `${percent}%`;
    progress.setAttribute('aria-valuenow', String(Math.round(percent)));

    if (time) {
      time.textContent = `${this.formatTime(current)} / ${this.formatTime(duration)}`;
    }
  }

  showControls(panel, autoHide = true) {
    if (!panel) return;

    clearTimeout(panel._videoFeatureControlsTimer);
    panel.classList.add('video-feature-is-controls-visible');

    const video = panel.querySelector('video');
    const isPlaying = video && !video.paused && !video.ended;

    if (autoHide && isPlaying) {
      panel._videoFeatureControlsTimer = setTimeout(() => {
        panel.classList.remove('video-feature-is-controls-visible');
      }, 2200);
    }
  }

  hideControls(panel) {
    if (!panel) return;
    clearTimeout(panel._videoFeatureControlsTimer);
    panel.classList.remove('video-feature-is-controls-visible');
  }

  syncState(panel) {
    const video = panel.querySelector('video');
    const isPlaying = !!video && !video.paused && !video.ended;

    panel.classList.toggle('video-feature-is-playing', isPlaying);
    panel.classList.toggle('video-feature-is-paused', !isPlaying);
    this.updateProgress(panel);

    if (isPlaying) this.showControls(panel, true);
  }

  async playPanel(panel, { reset = false, immediate = false } = {}) {
    const item = this.items[Number(panel.dataset.index)];
    const video = this.ensureVideo(panel, item);
    if (!video) return;

    if (reset) video.currentTime = 0;
    if (!immediate) await this.waitReady(video);

    video.play()
      .then(() => this.syncState(panel))
      .catch(() => this.syncState(panel));
  }

  pausePanel(panel, keepControls = true) {
    const video = panel.querySelector('video');
    if (!video) {
      this.syncState(panel);
      return;
    }

    video.pause();
    this.syncState(panel);
    keepControls ? this.showControls(panel, false) : this.hideControls(panel);
  }

  switchTo(index) {
    this.activeIndex = index;

    this.tabs.forEach(tab => {
      const active = Number(tab.dataset.index) === this.activeIndex;
      tab.classList.toggle('video-feature-is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });

    this.panels.forEach(panel => {
      const active = Number(panel.dataset.index) === this.activeIndex;
      panel.classList.toggle('video-feature-is-active', active);
      panel.setAttribute('aria-hidden', String(!active));

      if (active) {
        this.setPoster(panel, this.items[this.activeIndex]);
        this.playPanel(panel, { reset: false });
      } else {
        this.pausePanel(panel, false);
        this.unloadVideo(panel);
      }
    });

    this.updateIndicator();

    this.dispatchEvent(new CustomEvent('video-feature-change', {
      detail: {
        index: this.activeIndex,
        tab: this.activeTab,
        panel: this.activePanel,
        video: this.activePanel ? this.activePanel.querySelector('video') : null
      }
    }));
  }

  togglePanel(panel) {
    if (!panel.classList.contains('video-feature-is-active')) return;

    const item = this.items[Number(panel.dataset.index)];
    const video = this.ensureVideo(panel, item);
    if (!video) return;

    if (video.paused || video.ended) {
      this.playPanel(panel, { immediate: true });
    } else {
      this.pausePanel(panel);
    }
  }

  seek(panel, clientX) {
    const video = panel.querySelector('video');
    const progress = panel.querySelector('.video-feature-progress');
    if (!video || !progress || !video.duration) return;

    const rect = progress.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));

    video.currentTime = ratio * video.duration;
    this.updateProgress(panel);
  }

  onTabClick(event, index) {
    if (this.blockNextClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.switchTo(index);
  }

  bindPanel(panel) {
    const clickLayer = panel.querySelector('.video-feature-click-layer');
    const controls = panel.querySelector('.video-feature-controls');
    const playButton = panel.querySelector('.video-feature-control-play');
    const progress = panel.querySelector('.video-feature-progress');
    const fullscreen = panel.querySelector('.video-feature-control-fullscreen');

    if (!clickLayer) return;

    clickLayer.addEventListener('click', () => {
      this.enableActiveSound();
      this.togglePanel(panel);
      this.showControls(panel, true);
    });

    clickLayer.addEventListener('touchstart', () => {
      this.showControls(panel, true);
    }, { passive: true });

    controls?.addEventListener('click', event => {
      event.stopPropagation();
      this.showControls(panel, true);
    });

    controls?.addEventListener('pointerdown', event => {
      event.stopPropagation();
      this.showControls(panel, true);
    });

    playButton?.addEventListener('click', event => {
      event.stopPropagation();
      this.enableActiveSound();
      this.togglePanel(panel);
      this.showControls(panel, true);
    });

    progress?.addEventListener('pointerdown', event => {
      event.preventDefault();
      event.stopPropagation();
      this.enableActiveSound();
      this.seek(panel, event.clientX);
      this.showControls(panel, true);
      progress.setPointerCapture?.(event.pointerId);

      let seekFrame = null;
      let latestX = event.clientX;

      const requestSeek = () => {
        if (seekFrame) return;
        seekFrame = requestAnimationFrame(() => {
          this.seek(panel, latestX);
          this.showControls(panel, true);
          seekFrame = null;
        });
      };

      const onMove = moveEvent => {
        latestX = moveEvent.clientX;
        requestSeek();
      };

      const onEnd = () => {
        if (seekFrame) cancelAnimationFrame(seekFrame);
        progress.removeEventListener('pointermove', onMove);
        progress.removeEventListener('pointerup', onEnd);
        progress.removeEventListener('pointercancel', onEnd);
      };

      progress.addEventListener('pointermove', onMove);
      progress.addEventListener('pointerup', onEnd);
      progress.addEventListener('pointercancel', onEnd);
    });

    fullscreen?.addEventListener('click', event => {
      event.stopPropagation();
      this.enableActiveSound();
      document.fullscreenElement ? document.exitFullscreen?.() : this.stage.requestFullscreen?.();
    });
  }

  bindDragEvents() {
    this.scroller.addEventListener('pointerdown', event => {
      this.dragState = {
        startX: event.clientX,
        startScrollLeft: this.scroller.scrollLeft,
        dragging: false
      };
      this.blockNextClick = false;
    });

    this.scroller.addEventListener('pointermove', event => {
      if (!this.dragState) return;

      const deltaX = event.clientX - this.dragState.startX;

      if (!this.dragState.dragging && Math.abs(deltaX) > 6) {
        this.dragState.dragging = true;
        this.blockNextClick = true;
        this.scroller.classList.add('video-feature-is-dragging');
        this.scroller.setPointerCapture?.(event.pointerId);
      }

      if (!this.dragState.dragging) return;

      event.preventDefault();
      this.scroller.scrollLeft = this.dragState.startScrollLeft - deltaX;
      this.updateIndicator();
    });

    const endDrag = event => {
      if (!this.dragState) return;

      const wasDragging = this.dragState.dragging;
      this.dragState = null;

      if (!wasDragging) return;

      this.scroller.releasePointerCapture?.(event.pointerId);
      this.scroller.classList.remove('video-feature-is-dragging');

      setTimeout(() => {
        this.blockNextClick = false;
      }, 0);
    };

    this.scroller.addEventListener('pointerup', endDrag);
    this.scroller.addEventListener('pointercancel', endDrag);
    this.scroller.addEventListener('pointerleave', endDrag);
    this.scroller.addEventListener('scroll', () => this.updateIndicator(), { passive: true });
  }

  bindResizeEvents() {
    const update = () => {
      this.updateTabsAlign();
      this.updateIndicator();
    };

    window.addEventListener('resize', update);

    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(update);
      this._resizeObserver.observe(this);
      this._resizeObserver.observe(this.tabsWrap);
    }
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    this.panels?.forEach(panel => {
      clearTimeout(panel._videoFeatureControlsTimer);
      this.unloadVideo(panel);
    });
  }
}

if (!customElements.get('video-feature-tabs')) {
  customElements.define('video-feature-tabs', VideoFeatureTabs);
}
